// useAsyncOperation - poll a Tier 2 long-running operation to completion.
//
// A route that cannot finish inside the API Gateway window returns 202 + an
// operation_key in <1s. This hook polls /async_status every 3s until the
// row reaches a terminal state (completed | failed | cancelled) and returns
// the resolved operation record.
//
// 15-minute client-side timeout matches the BMA hook's shape and the
// Lambda's max runtime.
import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

export type AsyncOperationStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  // Terminal: the operator stopped the run (POST /cancel). Carries the
  // graceful-summary run_id when the agent loop had started, else no run.
  | "cancelled";

// Fine-grained progress for a list-iterating operation. ``total`` is null
// when not yet known (e.g. a Gmail search still gathering candidates) - the
// FE renders that as a count / indeterminate state rather than a bar.
export interface AsyncOperationProgress {
  done: number;
  total: number | null;
  label: string | null;
}

// One step in the live event feed for a running agent turn (interim "thinking",
// a tool call, a tool result). Appended as the turn runs so the FE can show what
// the agent is doing; transient (gone once the op completes - the AI trace is the
// after-the-fact record).
export interface AsyncOperationEvent {
  at: string;
  kind: string;
  text: string;
  // Which delegation this step belongs to, absent for the top-level agent's own
  // steps. Concurrent specialists interleave in one feed, so every line has to
  // say whose it is or the feed reads as one agent contradicting itself.
  group?: string;
  groupLabel?: string;
}

export interface AsyncOperationRecord {
  operation_key: string;
  project_id: string;
  operation_type:
    | "procurement_findings"
    | "procurement_cross_site_comparison"
    | "drive_sync_now"
    | "bma_refresh_all_sites"
    | "stateful_agent_turn"
    | "slack_sync";
  status: AsyncOperationStatus;
  triage: Record<string, unknown> | null;
  run_id: string | null;
  error: string | null;
  // True once the operator hit Stop but before the run has wound down to its
  // terminal `cancelled` state - the in-flight "Stopping..." signal. Lets the
  // FE render "Stopping..." durably (survives a reload mid-stop), not just from
  // local click state.
  cancel_requested?: boolean;
  progress?: AsyncOperationProgress | null;
  // Live event feed of the agent's steps during the run (interim thinking + tool
  // calls). Empty on operations that don't emit events.
  events?: AsyncOperationEvent[];
  created_at: string;
  updated_at: string;
}

const POLL_INTERVAL_MS = 3000;

const QUERY_KEY = QK.asyncOperation;

export const useAsyncOperation = (
  operation_key: string | null | undefined,
  opts: { enabled?: boolean } = {},
) => {
  const http = useAgentsHttp();
  const enabled = (opts.enabled ?? true) && Boolean(operation_key);

  return useQuery<AsyncOperationRecord>(
    [QUERY_KEY, operation_key ?? null],
    async () => {
      if (!operation_key) {
        throw new Error("operation_key required");
      }
      return http<AsyncOperationRecord>("/async_status", { operation_key });
    },
    {
      enabled,
      // Drive the poll cadence from the returned status: keep polling
      // while pending / running; stop the moment we see a terminal
      // state. ``refetchInterval`` returning ``false`` ends the loop
      // without forcing the caller to unmount the hook.
      refetchInterval: (data) => {
        if (!data) return POLL_INTERVAL_MS;
        if (data.status === "pending" || data.status === "running") {
          return POLL_INTERVAL_MS;
        }
        return false;
      },
      // Operations are intrinsically time-sensitive; never serve cached
      // data and never keep stale results in memory beyond the poll
      // window. Each new submit gets a clean cache entry keyed on its
      // own operation_key, so cache misses here are zero-cost.
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );
};

/** Whether an operation has stopped: the one definition every watcher uses, so a
 * cancelled operation can never leave a surface spinning. */
export const isOperationFinished = (status: AsyncOperationStatus | undefined): boolean =>
  status === "completed" || status === "failed" || status === "cancelled";

/** Poll an operation and call ``onFinished`` once when it stops, whatever way it
 * stopped. The caller clears its key there. */
export const useOperationWatcher = (
  operation_key: string | null | undefined,
  onFinished: (record: AsyncOperationRecord) => void,
) => {
  const query = useAsyncOperation(operation_key, { enabled: Boolean(operation_key) });
  const latest = useRef(onFinished);
  latest.current = onFinished;
  // Once per adoption: a key dropped and adopted again (a lagging in-flight read
  // re-reports a finished op) must reach onFinished again, or its caller stays busy.
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!operation_key) handled.current = null;
  }, [operation_key]);
  const record = query.data;
  useEffect(() => {
    if (!operation_key || !record || record.operation_key !== operation_key) return;
    if (!isOperationFinished(record.status) || handled.current === operation_key) return;
    handled.current = operation_key;
    latest.current(record);
  }, [operation_key, record]);
  return query;
};

// useCancelOperation - operator Stop for any in-flight operation. POSTs the
// operation_key to /cancel, which flips the cancel_requested flag; the worker
// reads it at the next turn boundary and winds down to a graceful summary
// (it does NOT kill the run mid-call). The poll then sees cancel_requested
// (so the FE can show "Stopping...") and finally the terminal `cancelled`
// status. Idempotent server-side, so a double-click is harmless.
export const useCancelOperation = () => {
  const http = useAgentsHttp();
  return useMutation<
    { operation_key: string; status: string; cancel_requested?: boolean },
    Error,
    { operation_key: string }
  >(async (input) =>
    http<{ operation_key: string; status: string; cancel_requested?: boolean }>(
      "/cancel",
      input,
    ),
  );
};

// useReleaseGate - a turn waiting for its documents continues without them, exactly
// as when the wait times out. The poll's `gate` disappears once the worker moves on.
export const useReleaseGate = () => {
  const http = useAgentsHttp();
  return useMutation<
    { operation_key: string; status: string; released: boolean },
    Error,
    { operation_key: string }
  >(async (input) =>
    http<{ operation_key: string; status: string; released: boolean }>(
      "/operations/release_gate",
      input,
    ),
  );
};
